import React, { useState } from 'react';
import { View, StyleSheet, ScrollView, Alert } from 'react-native';
import { Text, Card, Button, TextInput, Chip, Divider } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import api from '../../services/api';

const SolicitudDetalleScreen = ({ navigation, route }) => {
  const { solicitud } = route.params;
  const [loading, setLoading] = useState(false);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [showRechazoForm, setShowRechazoForm] = useState(false);

  const handleAprobar = () => {
    Alert.alert(
      'Aprobar Solicitud',
      `Esto creara la empresa "${solicitud.nombre_empresa}" con su usuario administrador. Deseas continuar?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Aprobar',
          onPress: async () => {
            setLoading(true);
            try {
              await api.superAdmin.aprobarSolicitud(solicitud.id);
              Alert.alert('Exito', 'Solicitud aprobada. La empresa y usuario admin han sido creados.', [
                { text: 'OK', onPress: () => navigation.goBack() }
              ]);
            } catch (error) {
              Alert.alert('Error', error.message || 'No se pudo aprobar la solicitud');
            } finally {
              setLoading(false);
            }
          },
        },
      ]
    );
  };

  const handleRechazar = async () => {
    if (!motivoRechazo.trim()) {
      Alert.alert('Error', 'Debes indicar un motivo de rechazo');
      return;
    }

    setLoading(true);
    try {
      await api.superAdmin.rechazarSolicitud(solicitud.id, motivoRechazo.trim());
      Alert.alert('Solicitud Rechazada', 'La solicitud ha sido rechazada.', [
        { text: 'OK', onPress: () => navigation.goBack() }
      ]);
    } catch (error) {
      Alert.alert('Error', error.message || 'No se pudo rechazar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const getEstadoConfig = (estado) => {
    const configs = {
      pendiente: { color: '#FF9800', icon: 'time-outline', label: 'Pendiente' },
      aprobada: { color: '#4CAF50', icon: 'checkmark-circle-outline', label: 'Aprobada' },
      rechazada: { color: '#f44336', icon: 'close-circle-outline', label: 'Rechazada' },
    };
    return configs[estado] || configs.pendiente;
  };

  const config = getEstadoConfig(solicitud.estado);

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {/* Estado */}
      <View style={styles.estadoCard}>
        <Ionicons name={config.icon} size={40} color={config.color} />
        <Text style={[styles.estadoText, { color: config.color }]}>{config.label}</Text>
        <Text style={styles.solicitudId}>Solicitud #{solicitud.id}</Text>
      </View>

      {/* Datos de la empresa */}
      <Card style={styles.card}>
        <Card.Content>
          <Text style={styles.cardTitle}>Datos de la Empresa</Text>
          <DetailRow label="Nombre" value={solicitud.nombre_empresa} />
          <DetailRow label="Codigo (slug)" value={solicitud.slug} />
          <DetailRow label="Telefono" value={solicitud.telefono} />
          <DetailRow label="Direccion" value={solicitud.direccion} />
          <DetailRow label="RTN" value={solicitud.rtn} />
        </Card.Content>
      </Card>

      {/* Datos del administrador / contacto (un solo email en tenant_solicitudes) */}
      <Card style={styles.card}>
        <Card.Content>
          <Text style={styles.cardTitle}>Administrador / Contacto</Text>
          <DetailRow label="Nombre" value={solicitud.nombre_contacto} />
          <DetailRow label="Email" value={solicitud.email} />
        </Card.Content>
      </Card>

      {/* Plan y fecha */}
      <Card style={styles.card}>
        <Card.Content>
          <Text style={styles.cardTitle}>Detalles</Text>
          <DetailRow label="Plan solicitado" value={solicitud.plan_nombre || `Plan ID: ${solicitud.plan_id}`} />
          <DetailRow
            label="Fecha solicitud"
            value={solicitud.created_at ? new Date(solicitud.created_at).toLocaleString('es-HN') : '-'}
          />
          {solicitud.notas && <DetailRow label="Notas" value={solicitud.notas} />}
        </Card.Content>
      </Card>

      {/* Notas admin (si rechazada) */}
      {solicitud.estado === 'rechazada' && solicitud.notas_admin && (
        <Card style={[styles.card, { backgroundColor: '#FFF3F0' }]}>
          <Card.Content>
            <Text style={[styles.cardTitle, { color: '#f44336' }]}>Motivo de Rechazo</Text>
            <Text style={styles.rejectText}>{solicitud.notas_admin}</Text>
          </Card.Content>
        </Card>
      )}

      {/* Acciones para pendientes */}
      {solicitud.estado === 'pendiente' && (
        <Card style={styles.card}>
          <Card.Content>
            <Text style={styles.cardTitle}>Acciones</Text>

            <Button
              mode="contained"
              onPress={handleAprobar}
              loading={loading}
              disabled={loading}
              style={[styles.actionButton, { backgroundColor: '#4CAF50' }]}
              icon="checkmark-circle-outline"
            >
              Aprobar Solicitud
            </Button>

            {!showRechazoForm ? (
              <Button
                mode="outlined"
                onPress={() => setShowRechazoForm(true)}
                style={[styles.actionButton, { borderColor: '#f44336' }]}
                textColor="#f44336"
                icon="close-circle-outline"
              >
                Rechazar Solicitud
              </Button>
            ) : (
              <View style={styles.rechazoForm}>
                <Divider style={{ marginBottom: 12 }} />
                <Text style={styles.rechazoLabel}>Motivo del rechazo:</Text>
                <TextInput
                  value={motivoRechazo}
                  onChangeText={setMotivoRechazo}
                  style={styles.input}
                  multiline
                  numberOfLines={3}
                  placeholder="Indica el motivo del rechazo..."
                />
                <View style={styles.rechazoButtons}>
                  <Button
                    mode="text"
                    onPress={() => { setShowRechazoForm(false); setMotivoRechazo(''); }}
                    style={{ flex: 1 }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    mode="contained"
                    onPress={handleRechazar}
                    loading={loading}
                    disabled={loading}
                    style={{ flex: 1, backgroundColor: '#f44336' }}
                  >
                    Confirmar Rechazo
                  </Button>
                </View>
              </View>
            )}
          </Card.Content>
        </Card>
      )}
    </ScrollView>
  );
};

const DetailRow = ({ label, value }) => (
  <View style={styles.detailRow}>
    <Text style={styles.detailLabel}>{label}</Text>
    <Text style={styles.detailValue}>{value || '-'}</Text>
  </View>
);

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  content: { padding: 16, paddingBottom: 40 },
  estadoCard: { backgroundColor: 'white', borderRadius: 12, padding: 20, alignItems: 'center', marginBottom: 12, elevation: 1 },
  estadoText: { fontSize: 20, fontWeight: 'bold', marginTop: 8 },
  solicitudId: { fontSize: 14, color: '#888', marginTop: 4 },
  card: { marginBottom: 12, backgroundColor: 'white' },
  cardTitle: { fontSize: 16, fontWeight: 'bold', color: '#333', marginBottom: 12 },
  detailRow: { flexDirection: 'row', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f5f5f5' },
  detailLabel: { fontSize: 14, color: '#666', width: 120 },
  detailValue: { fontSize: 14, color: '#333', fontWeight: '500', flex: 1 },
  rejectText: { fontSize: 14, color: '#666', lineHeight: 20 },
  actionButton: { marginBottom: 10 },
  rechazoForm: { marginTop: 8 },
  rechazoLabel: { fontSize: 14, fontWeight: '600', color: '#f44336', marginBottom: 8 },
  input: { marginBottom: 12, backgroundColor: '#f9f9f9' },
  rechazoButtons: { flexDirection: 'row', gap: 10 },
});

export default SolicitudDetalleScreen;
